import { toast } from "@components/utils/Toaster";
import { AnimationDefinition, motion } from "framer-motion";
import { PropsWithClassName, cn } from "kz-ui-sdk";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ShimmerBadgeFTD from "./ShimmerBadgeFTD";
import { ANIMATION_STATE, LOOP_CONFIG, ftdGiftTextVariant } from "./utils";

interface FTDGiftTextProps extends PropsWithClassName {
  zIndex: number;
  onAnimationStateChange?: (state: ANIMATION_STATE) => void;
}

const FTDGiftText = ({ className, zIndex, onAnimationStateChange }: FTDGiftTextProps) => {
  const { t } = useTranslation();
  const [animState, setAnimState] = useState<ANIMATION_STATE>(ANIMATION_STATE.ENTERING);
  const pulseTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setAnimState(ANIMATION_STATE.ENTERING);

    return () => {
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    };
  }, []);

  // Notify parent of animation state changes
  useEffect(() => {
    onAnimationStateChange?.(animState);
  }, [animState, onAnimationStateChange]);

  const handleAnimationComplete = (definition: AnimationDefinition) => {
    if (definition === ANIMATION_STATE.ENTERING) {
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
      pulseTimerRef.current = setTimeout(() => {
        setAnimState(ANIMATION_STATE.PULSING);
      }, LOOP_CONFIG.PULSE_HOLD_DELAY);
    }
  };

  const handleClick = () => {
    toast.info(t("deposit and receive a free gift"), {
      duration: 5000, // 5 seconds
      icon: (
        <img
          src="/images/icon-gift-box-blue.png"
          alt=""
          className="h-4 w-4 text-informative-600"
          aria-hidden="true"
        />
      ),
    });
  };

  return (
    <motion.div
      className={cn("relative w-fit cursor-pointer", className)}
      style={{ pointerEvents: "auto" }}
      variants={ftdGiftTextVariant}
      initial={ANIMATION_STATE.IDLE}
      animate={animState}
      onAnimationComplete={handleAnimationComplete}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-label={t("deposit and receive a free gift")}
    >
      <div
        className="relative flex w-fit flex-col items-center pt-5"
        style={{ zIndex }}
      >
        <div className="absolute left-1/2 top-1 z-20 -translate-x-1/2">
          <img
            src="/images/skinner-box/bg-gift-glow.png"
            alt=""
            className="absolute left-1/2 top-[60%] z-0 h-[46px] w-[46px] max-w-none -translate-x-1/2 -translate-y-1/2"
            aria-hidden="true"
          />
          <img
            src="/images/icon-gift-box-no-shadow.png"
            alt="Gift box"
            className="relative z-10 h-[30px] w-[26px]"
            aria-label="FTD gift box"
          />
        </div>

        {/* Text Banner with SVG-based shimmer */}
        <ShimmerBadgeFTD isShimmering={animState === ANIMATION_STATE.PULSING} />
      </div>
    </motion.div>
  );
};

export default FTDGiftText;
